import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Anchor, ActionIcon, Badge, Box, Button, Card, Code, CopyButton, Flex, Grid, Switch, Text, Tooltip } from '@mantine/core';
import {
  IconCalendar,
  IconCheck,
  IconCopy,
  IconChartLine,
  IconClock,
  IconDeviceDesktop,
  IconFile,
  IconGripVertical,
  IconHistory,
  IconPackages,
  IconSettings,
  IconUsers,
} from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import HeaderStatistic from '../../../components/HeaderStatistic';
import LazyLoadSpinner from '../../../components/LazyLoaderSpinner';
import Metadata from '../../../components/Metadata';
import PriorityOrderBar from '../../../components/PriorityOrderBar';
import RecommenderPageUsage from '../../../components/Recommenders/RecommenderPageUsage';
import Spinner from '../../../components/Spinner';
import prettifiedLabel from '../../../constants/labelPrettifier';
import { Page } from '../../../constants/pageTitles';
import {
  isRecommenderPageLimitError,
  recommenderPageLimitReason,
  showRecommenderPageLimitNotification,
} from '../../../helpers/recommenderPageLimits';
import useFetch from '../../../helpers/useFetch';

const SortableItem = ({
  recommender,
  statsElements,
  linkToSegment,
  toggleRecommender,
  isShopify,
  priority,
  enableBlockedReason,
}) => {
  const { accountId, domainId } = useParams();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: recommender.id.toString(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const location = useLocation();

  const now = new Date();
  const isExpired = recommender.endTime && new Date(recommender.endTime) < now;
  const isScheduled = recommender.running && recommender.startTime && new Date(recommender.startTime) > now;
  const hasActiveEndDate = recommender.running && recommender.endTime && new Date(recommender.endTime) > now;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card mb="md" data-tour={recommender.name === 'Recommended for You' ? 'recommenderForYou' : ''} withBorder>
        <Card.Section inheritPadding py="sm">
          <Flex gap="md" align="flex-start">
            <Tooltip label={`Priority ${priority}`} position="left" withArrow>
              <Box {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', gap: 4, paddingTop: 4 }}>
                <Text size="sm" fw={700} c="dimmed" style={{ minWidth: 20, textAlign: 'center' }}>{priority}</Text>
                <IconGripVertical size={20} color="var(--mantine-color-dimmed)" />
              </Box>
            </Tooltip>
            <Box style={{ flex: 1 }}>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Anchor
                    href={`#${location.pathname}/${recommender.pageId}/recommenders/${recommender.id}/details`}
                    fw={600}
                    mb="xs"
                    style={{ display: 'block' }}
                  >
                    {recommender.name}
                  </Anchor>
                  {isShopify && recommender.token && (
                    <Flex gap="xs" wrap="wrap" mb="xs" align="center">
                      <Text size="xs" c="dimmed">Token: <Code>{recommender.token}</Code></Text>
                      <CopyButton value={recommender.token}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied' : 'Copy token'} withArrow>
                            <ActionIcon size="xs" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Flex>
                  )}
                  <Flex gap="xs" wrap="wrap">
                    <Badge variant="default">
                      <IconDeviceDesktop size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {prettifiedLabel(recommender.displayType)}
                    </Badge>
                    <Badge variant="default">
                      <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <Anchor href={linkToSegment} target="_blank" size="xs">
                        {recommender.segment?.name || 'Unknown'}{' '}
                        {recommender.inverseSegmentSelection ? '(exclude)' : ''}
                      </Anchor>
                    </Badge>
                    <Badge variant="default">
                      <IconPackages size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {recommender.productFeed ? (
                        <Anchor
                          href={`#/accounts/${accountId}/domains/${domainId}/productFeeds/${recommender.productFeed.id}/details`}
                          size="xs"
                        >
                          {recommender.productFeed.name}
                        </Anchor>
                      ) : (
                        'Unknown'
                      )}
                    </Badge>
                    {recommender.template && (
                      <Badge variant="default">
                        <IconFile size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        <Anchor
                          href={`#/accounts/${accountId}/domains/${domainId}/templates/${recommender.template.id}/details`}
                          size="xs"
                        >
                          {recommender.template.name}
                        </Anchor>
                      </Badge>
                    )}
                    <Badge variant="default">
                      <IconSettings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {prettifiedLabel(recommender.mode)}
                    </Badge>
                    <Tooltip
                      label={
                        <>
                          <div>Created: {new Date(recommender.createdAt).toLocaleString()}</div>
                          <div>Updated: {new Date(recommender.updatedAt).toLocaleString()}</div>
                        </>
                      }
                      position="top"
                      withArrow
                    >
                      <Badge variant="default" style={{ cursor: 'pointer' }}>
                        <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                      </Badge>
                    </Tooltip>
                    {recommender.startTime && (
                      <Badge variant="default">
                        <IconCalendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Starts: {new Date(recommender.startTime).toLocaleString()}
                      </Badge>
                    )}
                    {isExpired && recommender.endTime && (
                      <Badge variant="default">
                        <IconCalendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Ended: {new Date(recommender.endTime).toLocaleString()}
                      </Badge>
                    )}
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Flex justify="flex-end" align="center" gap="sm" h="100%">
                    <Button
                      component="a"
                      href={`#${location.pathname}/${recommender.pageId}/recommenders/${recommender.id}/analytics`}
                      size="xs"
                    >
                      Statistics <IconChartLine size={14} style={{ marginLeft: 4 }} />
                    </Button>
                    <Box data-tour={recommender.name === 'Recommended for You' ? 'toggleRecommenderForYou' : ''}>
                      {isExpired ? (
                        <Flex align="center" gap="xs">
                          <Button
                            component="a"
                            href={`#${location.pathname}/${recommender.pageId}/recommenders/${recommender.id}/details`}
                            size="xs"
                          >
                            Run Again
                          </Button>
                          <Text fw={700} c="red">EXPIRED</Text>
                        </Flex>
                      ) : (
                        <Flex align="center" justify="flex-end" gap="xs">
                          {isScheduled && (
                            <Flex align="center" display="inline-flex" gap={4}>
                              <IconClock size={20} />
                              <Text component="span" visibleFrom="md">Scheduled</Text>
                            </Flex>
                          )}
                          {hasActiveEndDate && (
                            <Flex align="center" display="inline-flex" gap={4}>
                              <IconClock size={20} />
                              <Text component="span" visibleFrom="md">
                                Ends {new Date(recommender.endTime).toLocaleDateString()}
                              </Text>
                            </Flex>
                          )}
                          <Tooltip label={enableBlockedReason} disabled={!enableBlockedReason} withArrow>
                            <Box>
                              <Switch
                                size="lg"
                                checked={recommender.running}
                                onChange={toggleRecommender}
                                disabled={!!enableBlockedReason}
                              />
                            </Box>
                          </Tooltip>
                        </Flex>
                      )}
                    </Box>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          </Flex>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Grid>
            <Grid.Col span={12}>
              <Grid>{statsElements}</Grid>
            </Grid.Col>
          </Grid>
        </Card.Section>
      </Card>
    </div>
  );
};

const RecommendersPage = ({ page, updatePage, stats, filter, usage, refreshUsage }) => {
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleRecommender = (recommenderIndex, running) => {
    const recommender = page.recommenders[recommenderIndex];
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/pages/${recommender.pageId}/recommenders/${recommender.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ running }),
      }
    )
      .then((result) => {
        const modifiedRecommenders = [...page.recommenders];
        modifiedRecommenders[recommenderIndex] = result.recommender;
        updatePage({ ...page, recommenders: modifiedRecommenders });
        refreshUsage();
      })
      .catch((err) => {
        if (isRecommenderPageLimitError(err)) {
          showRecommenderPageLimitNotification(err);
          refreshUsage();
        }
      });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id === over.id) {
      return;
    }

    const oldIndex = page.recommenders.map((b) => b.id.toString()).indexOf(active.id);
    const newIndex = page.recommenders.map((b) => b.id.toString()).indexOf(over.id);
    const originalRecommenders = [...page.recommenders];
    const rearrangedRecommenders = arrayMove(page.recommenders, oldIndex, newIndex);
    updatePage({ ...page, recommenders: rearrangedRecommenders });

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${page.id}/recommenders/rearrange`, {
      method: 'POST',
      body: JSON.stringify({ recommenders: rearrangedRecommenders }),
    })
      .then(() => {})
      .catch(() => {
        updatePage({ ...page, recommenders: originalRecommenders });
      });
  };

  if (!page) {
    return <Spinner />;
  }

  const limitReason = recommenderPageLimitReason(usage);
  const now = new Date();
  const filteredRecommenders = page.recommenders.filter((r) => {
    if (filter === 'expired') return r.endTime && new Date(r.endTime) < now;
    if (filter === 'scheduled') return r.running && r.startTime && new Date(r.startTime) > now;
    return true;
  });

  let recommenderElements = [];
  for (let j = 0; j < filteredRecommenders.length; j++) {
    const recommender = filteredRecommenders[j];
    let statsElements;
    if (!stats) {
      statsElements = (
        <Grid.Col span={12}>
          <Grid justify="center">
            <LazyLoadSpinner />
          </Grid>
        </Grid.Col>
      );
    } else {
      statsElements = (
        <>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="pageviewStats">
            <HeaderStatistic title="Page Views" stats={stats} stat="views" id={page.token} statKey="count" />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="recommenderImpressionStats">
            <HeaderStatistic
              title="Impressions"
              stats={stats}
              stat="impressions"
              percentageOfStat="views"
              percentageOfId={page.token}
              id={recommender.token}
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="recommenderClicksStats">
            <HeaderStatistic
              title="Clicks"
              stats={stats}
              stat="clicks"
              id={recommender.token}
              percentageOfStat="impressions"
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="recommenderConversionsStats">
            <HeaderStatistic
              title="Products to Cart"
              stats={stats}
              stat="conversions"
              percentageOfStat="clicks"
              id={recommender.token}
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="recommenderPurchasesStats">
            <HeaderStatistic
              title="Purchased Products"
              stats={stats}
              stat="purchases"
              percentageOfStat="conversions"
              id={recommender.token}
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }} data-tour="recommenderRevenueStats">
            <HeaderStatistic
              title="Revenue"
              stats={stats}
              stat="purchases"
              id={recommender.token}
              statKey="value"
              currency={domainCurrency}
            />
          </Grid.Col>
        </>
      );
    }
    const linkToSegment = recommender.segment
      ? `#/accounts/${accountId}/domains/${domainId}/segments/${recommender.segment.id}`
      : null;

    recommenderElements.push(
      <SortableItem
        key={`recommender-${recommender.id}`}
        index={j}
        priority={page.recommenders.indexOf(recommender) + 1}
        recommender={recommender}
        linkToSegment={linkToSegment}
        toggleRecommender={(e) => toggleRecommender(page.recommenders.indexOf(recommender), e.target.checked)}
        statsElements={statsElements}
        isShopify={currentDomain.platform === 'shopify'}
        // Turning a block off never grows the page, so it stays available on a
        // page that is already at or over a limit.
        enableBlockedReason={recommender.running ? null : limitReason}
      />
    );
  }

  return (
    <>
      <Metadata title={`${Page['recommenders']}`} />
      <RecommenderPageUsage usage={usage} />
      {filteredRecommenders.length > 1 && <PriorityOrderBar />}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div data-tour="recommendersForPage">
          <SortableContext
            items={page.recommenders.map((r) => r.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            {recommenderElements}
          </SortableContext>
        </div>
      </DndContext>
    </>
  );
};

export default RecommendersPage;
